import { useCallback, useEffect, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { apiGet, adminMutate, useToast, usePermissions } from '../admin/primitives.js'
import { ageCourt } from '../hubs/FamilyHub.js'

const SOURCE_LABELS: Record<string, string> = { SKELLO: 'Skello', UNIFI_ACCESS: 'UniFi Access' }

/**
 * SECTION Synchronisations Skello/UniFi — consolidation SIRH : cette surface
 * TECHNIQUE vit désormais dans Administration → Intégrations → Skello, plus
 * jamais dans la navigation RH quotidienne (§7 cleanup).
 */

interface SyncData {
  sources: {
    source: 'SKELLO' | 'UNIFI_ACCESS'
    refresh_interval_s: number
    stale_after_s: number
    last_attempt_at: string | null
    last_success_at: string | null
    seconds_since_success: number | null
    stale: boolean
    consecutive_failures: number
    error_class: string | null
    objects_seen: number | null
    refresh_pending: boolean
  }[]
  runs: { id: string; type: string; mode: string; status: string; started_at: string; duration_ms: number | null; planned: Record<string, unknown> | null }[]
}

export function SkelloSyncSection() {
  const [d, setD] = useState<SyncData | null>(null)
  const toast = useToast()
  const { permissions, authenticated } = usePermissions()
  const canRefresh = authenticated && permissions.includes('sync.run')

  const charger = useCallback(() => {
    apiGet<SyncData>('/api/v1/rh/sync').then((r) => { if ('sources' in r) setD(r) }).catch(() => undefined)
  }, [])
  useEffect(() => {
    charger()
    const t = setInterval(charger, 15_000)
    return () => clearInterval(t)
  }, [charger])

  const rafraichir = async (source: string) => {
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/rh/sync/${source}/refresh`)
    if (r.status < 300) { toast('ok', 'Actualisation demandée — le résultat arrive dans quelques secondes.'); setTimeout(charger, 4000) }
    else toast('error', r.data.message ?? 'Actualisation refusée.')
  }

  return (
    <div className="lc-stagger">
      <p className="lc-subtitle">Le worker Gestion lit Skello et UniFi Access — jamais le navigateur, jamais d’écriture.</p>

      <div className="rh-cards">
        {(d?.sources ?? []).map((s) => (
          <section key={s.source} className="lc-panel gd-panel rh-synccard">
            <div className="lc-panel__body">
              <header className="rh-synccard__head">
                <span className={`lc-dot ${s.error_class || s.stale ? 'lc-dot--warning' : 'lc-dot--ok'}`} aria-hidden="true" />
                <h2 className="lc-section-title" style={{ margin: 0 }}>{SOURCE_LABELS[s.source]}</h2>
                {canRefresh ? (
                  <button className="lc-btn lc-btn--ghost" disabled={s.refresh_pending} onClick={() => void rafraichir(s.source === 'SKELLO' ? 'skello' : 'unifi-access')}>
                    <RefreshCw size={14} aria-hidden="true" /> {s.refresh_pending ? 'En cours…' : 'Actualiser'}
                  </button>
                ) : null}
              </header>
              <div className="ad-facts lc-panel__body--flush" style={{ marginTop: 'var(--sp-3)' }}>
                <div className="ad-fact"><span className="ad-fact__label">Dernière réussite</span><span className="ad-fact__value" style={{ fontWeight: 400 }}>{s.last_success_at ? `${ageCourt(s.seconds_since_success)}` : 'Jamais'}</span></div>
                <div className="ad-fact"><span className="ad-fact__label">Objets lus</span><span className="ad-fact__value">{s.objects_seen ?? '—'}</span></div>
                <div className="ad-fact">
                  <span className="ad-fact__label lc-stack"><span>Cadence</span><span className="lc-hint">Puis considéré ancien après {Math.round(s.stale_after_s / 60)} min.</span></span>
                  <span className="ad-fact__value" style={{ fontWeight: 400 }}>toutes les {Math.round(s.refresh_interval_s / 60)} min</span>
                </div>
                {s.error_class ? (
                  <div className="ad-fact">
                    <span className="ad-fact__label lc-stack"><span>Dernier problème</span><span className="lc-hint">Les dernières données valides restent affichées.</span></span>
                    <span className="ad-fact__value"><span className="lc-pill lc-pill--warning">{s.error_class}</span></span>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        ))}
      </div>

      <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }} aria-label="Historique">
        <header className="lc-panel__head"><h2 className="lc-section-title">Dernières synchronisations</h2></header>
        <table className="lc-table">
          <thead><tr><th>Quand</th><th>Source</th><th>Déclenchement</th><th>Résultat</th><th>Lecture</th><th>Durée</th></tr></thead>
          <tbody>
            {(d?.runs ?? []).map((r) => {
              const c = (r.planned ?? {}) as { read?: number; created?: number; updated?: number; missing?: number; error?: string }
              return (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.started_at).toLocaleTimeString('fr-FR')}</td>
                  <td>{r.type === 'RH_SKELLO_READ' ? 'Skello' : 'UniFi Access'}</td>
                  <td>{r.mode === 'MANUAL' ? 'Manuel' : 'Automatique'}</td>
                  <td>{r.status === 'SUCCESS' ? <span className="lc-pill lc-pill--ok">OK</span> : <span className="lc-pill lc-pill--warning">Échec</span>}</td>
                  <td className="lc-hint">{c.read !== undefined ? `${c.read} lus · ${c.created ?? 0} nouveaux · ${c.updated ?? 0} à jour${c.missing ? ` · ${c.missing} absents` : ''}` : (c.error ?? '—')}</td>
                  <td className="lc-hint">{r.duration_ms !== null ? `${(r.duration_ms / 1000).toFixed(1)} s` : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>
    </div>
  )
}
